import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { QrCode, Lock, ArrowRight } from "lucide-react";
import GoldenParticles from "@/components/GoldenParticles";

const ease = [0.22, 1, 0.36, 1];

export default function VisitorLanding() {
  return (
    <div className="relative min-h-screen bg-[#050505] text-white overflow-hidden">
      {/* Ambient gold glow */}
      <div
        className="absolute -top-[280px] left-1/2 -translate-x-1/2 w-[1200px] h-[1200px] rounded-full blur-3xl opacity-[0.18] pointer-events-none"
        style={{ background: "#D4AF37" }}
      />
      <div className="absolute inset-0 grain opacity-40 pointer-events-none" />
      <GoldenParticles count={16} density="normal" />

      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6 py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease }}
          className="max-w-xl w-full text-center"
          data-testid="visitor-landing"
        >
          {/* Tag */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="text-[10px] font-bold uppercase tracking-[0.45em] mb-8 text-gradient-gold"
          >
            Laser Tech Création · Genève
          </motion.div>

          {/* QR icon with halo */}
          <motion.div
            initial={{ scale: 0.85, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.7, ease }}
            className="relative w-28 h-28 mx-auto mb-10"
          >
            <motion.div
              animate={{ opacity: [0.4, 0.85, 0.4] }}
              transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -inset-6 rounded-full"
              style={{
                background:
                  "radial-gradient(circle, rgba(249,223,159,0.35) 0%, rgba(212,175,55,0.12) 40%, rgba(5,5,5,0) 75%)",
                filter: "blur(6px)",
              }}
            />
            <div
              className="relative w-28 h-28 rounded-3xl flex items-center justify-center gold-rim"
              style={{
                background:
                  "linear-gradient(180deg, rgba(212,175,55,0.12) 0%, rgba(212,175,55,0.04) 100%)",
              }}
            >
              <QrCode
                className="w-12 h-12"
                style={{ color: "#D4AF37", strokeWidth: 1.5 }}
              />
            </div>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.7, ease }}
            className="font-display text-5xl sm:text-6xl md:text-7xl font-semibold uppercase tracking-tight leading-[0.9] mb-6"
            style={{ filter: "drop-shadow(0 4px 18px rgba(212,175,55,0.15))" }}
          >
            Scannez
            <br />
            <span className="text-gradient-gold">votre médaille</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.6, ease }}
            className="text-zinc-400 font-body text-base sm:text-lg leading-relaxed mb-12 max-w-md mx-auto"
          >
            Cette plateforme est <span className="text-white">privée</span> et
            réservée aux médailles, trophées et plaques Laser Tech Création.
            Approchez votre téléphone NFC ou scannez le QR code pour découvrir
            la page connectée.
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.65, duration: 0.55, ease }}
            className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4"
          >
            <Link
              to="/showcase"
              data-testid="visitor-showcase-link"
              className="btn-gold-solid inline-flex items-center gap-2 px-7 py-3 rounded-full text-[11px] uppercase tracking-[0.22em] font-bold"
            >
              Découvrir nos médailles
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
            <Link
              to="/login"
              data-testid="visitor-admin-link"
              className="btn-gold-outline inline-flex items-center gap-2 px-7 py-3 rounded-full text-[11px] uppercase tracking-[0.22em] font-bold"
            >
              <Lock className="w-3 h-3" /> Accès administrateur
            </Link>
          </motion.div>

          {/* Footer signature */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1, duration: 0.6 }}
            className="mt-16 text-[10px] uppercase tracking-[0.35em] text-zinc-700"
          >
            Made in Geneva
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}
